/**
 * Safe Area Styles
 * Shared styles for handling safe area insets across platforms
 */

import { StyleSheet, Platform, ViewStyle } from 'react-native';

/**
 * Minimum bottom padding to ensure touch targets are accessible
 * and provide visual breathing room above the navigation bar
 * even when safe area insets report 0 (e.g., some Android devices)
 */
export const SAFE_AREA_MINIMUM_BOTTOM_PADDING = 40;

/**
 * Resolves bottom safe padding for footers and pinned bottom chrome.
 * iOS: use the device inset only (home indicator). Android: enforce a minimum
 * when insets report 0 (gesture/nav bar variance).
 */
export const resolveBottomSafePadding = (bottomInset: number): number => {
  const normalizedInset = Math.max(bottomInset, 0);

  if (Platform.OS === 'ios') {
    return normalizedInset;
  }

  return Math.max(normalizedInset, SAFE_AREA_MINIMUM_BOTTOM_PADDING);
};

/**
 * Creates bottom padding style based on safe area insets
 * @param bottomInset - The bottom inset value from useSafeAreaInsets()
 * @returns ViewStyle with appropriate paddingBottom
 */
export const createBottomPaddingStyle = (bottomInset: number): ViewStyle => ({
  paddingBottom: resolveBottomSafePadding(bottomInset),
});

/**
 * Platform-specific base styles for safe area containers
 */
export const safeAreaStyles = StyleSheet.create({
  bottomContainer: {
    ...Platform.select({
      android: {
        elevation: 4,
      },
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      default: {},
    }),
  },
});
